import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase, type CDNConfig } from './configs';
import type { CDN } from './shared-cdns-types';

// ============================================
// Query keys
// ============================================

export const sharedCDNKeys = {
  list: (prefix: string, serviceId: string) =>
    [prefix, 'cdns', { serviceId }] as const,
  detail: (prefix: string, cdnId: string | number, serviceId: string) =>
    [prefix, 'cdn_detail', { cdnId, serviceId }] as const,
};

// ============================================
// Queries
// ============================================

export function useCDNs(serviceId: string, config: CDNConfig) {
  return useQuery<CDN[]>({
    queryKey: sharedCDNKeys.list(config.queryKeyPrefix, serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, config.kind)}/cdns`);
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useCDNDetail(
  serviceId: string,
  config: CDNConfig,
  cdnId: string | number | null,
) {
  return useQuery<CDN>({
    queryKey: sharedCDNKeys.detail(config.queryKeyPrefix, cdnId ?? '', serviceId),
    enabled: !!serviceId && !!cdnId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, config.kind)}/cdns/${cdnId}`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateCDN(serviceId: string, config: CDNConfig) {
  const qc = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<CDN, Error, any>({
    mutationFn: async (payload) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, config.kind)}/cdns`, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedCDNKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}

export function useUpdateCDN(serviceId: string, config: CDNConfig) {
  const qc = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<CDN, Error, { cdnId: string | number; payload: any }>({
    mutationFn: async ({ cdnId, payload }) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, config.kind)}/cdns/${cdnId}`, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedCDNKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}

export function useDeleteCDN(serviceId: string, config: CDNConfig) {
  const qc = useQueryClient();
  return useMutation<void, Error, { cdnId: string | number }>({
    mutationFn: async ({ cdnId }) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, config.kind)}/cdns/${cdnId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedCDNKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}
